import React from 'react';
import { FormattedMessage } from 'react-intl';

// @mui/material components
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import CtaLinkRow from './CtaLinkRow';
import { createEmptyCtaLink, moveCtaLink } from './helpers';

/**
 * Controlled "CTA links" section (value/onChange): plugs into redux-form (categories) as well
 * as Formik (corners, later) — the array order is the display order (#2378).
 *
 * @param {Array} value - list of CTA links
 * @param {function} onChange - receives the new list
 * @param {Array} locales - organization locales, as returned by localeData(): {code, name, flag}
 */
export default function CtaLinksSection({ value, onChange, locales }) {
    const ctaLinks = value ?? [];
    const localeCodes = locales.map((locale) => locale.code);

    const replaceAt = (index, ctaLink) => {
        const updated = [...ctaLinks];
        updated[index] = ctaLink;
        onChange(updated);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component="h4">
                <FormattedMessage id="category.form.ctaLinks.title" />
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                <FormattedMessage id="category.form.ctaLinks.description" />
            </Typography>
            {ctaLinks.map((ctaLink, index) => (
                <CtaLinkRow
                    // Stable client-side id (never submitted): CtaLinkRow holds local state
                    // (hasBlurred), an index key would attach it to the wrong row after a move.
                    key={ctaLink.clientId}
                    ctaLink={ctaLink}
                    locales={locales}
                    onChange={(updatedLink) => replaceAt(index, updatedLink)}
                    onDelete={() => onChange(ctaLinks.filter((candidate, candidateIndex) => candidateIndex !== index))}
                    onMoveUp={() => onChange(moveCtaLink(ctaLinks, index, -1))}
                    onMoveDown={() => onChange(moveCtaLink(ctaLinks, index, 1))}
                    canMoveUp={index > 0}
                    canMoveDown={index < ctaLinks.length - 1}
                />
            ))}
            <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onChange([...ctaLinks, createEmptyCtaLink(localeCodes)])}
            >
                <FormattedMessage id="category.form.ctaLinks.add" />
            </Button>
        </Box>
    );
}
